import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import WorkspaceSidebar from '../../components/ui/WorkspaceSidebar';
import UserProfileDropdown from '../../components/ui/UserProfileDropdown';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import DocumentTemplateCard from './components/DocumentTemplateCard';
import DocumentConfigPanel from './components/DocumentConfigPanel';
import DiagramEditor from './components/DiagramEditor';
import DocumentPreview from './components/DocumentPreview';
import VersionControlPanel from './components/VersionControlPanel';

const DocumentGenerator = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [documentConfig, setDocumentConfig] = useState({
    organizationName: 'Enterprise Solutions Ltd',
    projectName: 'SAP S/4HANA Implementation',
    version: '1.0',
    author: 'SAP Architect'
  });
  const [activeView, setActiveView] = useState('templates');
  const [diagramData, setDiagramData] = useState('');
  const navigate = useNavigate();

  const templates = [
    {
      id: 'bpd-standard',
      name: 'Standard BPD² Template',
      type: 'BPD',
      description: 'Comprehensive Business Process Definition document template with SAP Best Practice integration, requirement traceability, and approval workflows.',
      version: '2.1',
      lastUpdated: 'Oct 8, 2024',
      status: 'Active',
      usageCount: 127
    },
    {
      id: 'fsd-technical',
      name: 'Technical FSD² Template',
      type: 'FSD',
      description: 'Functional Specification Document template focusing on technical implementation details, RICEFW objects, and system integration requirements.',
      version: '1.8',
      lastUpdated: 'Oct 5, 2024',
      status: 'Active',
      usageCount: 89
    },
    {
      id: 'bpd-agile',
      name: 'Agile BPD² Template',
      type: 'BPD',
      description: 'Streamlined Business Process Definition template optimized for agile SAP implementations with iterative approval cycles.',
      version: '1.3',
      lastUpdated: 'Sep 28, 2024',
      status: 'Beta',
      usageCount: 34
    },
    {
      id: 'fsd-integration',
      name: 'Integration FSD² Template',
      type: 'FSD',
      description: 'Specialized Functional Specification Document for SAP integration scenarios with external systems and data migration requirements.',
      version: '2.0',
      lastUpdated: 'Oct 1, 2024',
      status: 'Active',
      usageCount: 56
    }
  ];

  const viewOptions = [
    { id: 'templates', label: 'Templates', icon: 'FileText' },
    { id: 'editor', label: 'Editor', icon: 'Edit3' },
    { id: 'preview', label: 'Preview', icon: 'Eye' },
    { id: 'versions', label: 'Versions', icon: 'GitBranch' }
  ];

  useEffect(() => {
    // Auto-select first template for demo
    if (templates?.length > 0 && !selectedTemplate) {
      setSelectedTemplate(templates?.[0]);
    }
  }, []);

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setActiveView('editor');
  };

  const handleConfigChange = (newConfig) => {
    setDocumentConfig(newConfig);
  };

  const handleDiagramChange = (diagramXML) => {
    setDiagramData(diagramXML);
  };

  const handleExport = (format) => {
    console.log(`Exporting document as ${format}`);
    if (format === 'approval') {
      navigate('/digital-signoffs');
    }
  };

  const handleVersionSelect = (version) => {
    console.log('Selected version:', version);
  };

  const handleGenerateDocument = () => {
    if (!selectedTemplate) {
      alert('Please select a template first');
      return;
    }
    setActiveView('preview');
  };

  const handleSaveDraft = () => {
    console.log('Saving draft document');
    // In real implementation, this would save to backend
  };

  return (
    <div className="min-h-screen bg-background">
      <WorkspaceSidebar 
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
      />
      <div className={`transition-all duration-300 ${sidebarCollapsed ? 'ml-16' : 'ml-64'} md:ml-64`}>
        {/* Header */}
        <header className="bg-card border-b border-border px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                  <Icon name="FileText" size={24} className="text-primary-foreground" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-foreground">Document Generator</h1>
                  <p className="text-sm text-muted-foreground">
                    Create BPD² and FSD² documents with templating and version control
                  </p>
                </div>
              </div>
            </div>
            <UserProfileDropdown />
          </div>
        </header>

        {/* Main Content */}
        <main className="p-6 pb-20 md:pb-6">
          <BreadcrumbNavigation />

          {/* View Navigation */}
          <div className="flex items-center justify-between mb-6">
            <nav className="flex space-x-1 bg-muted p-1 rounded-lg">
              {viewOptions?.map((view) => (
                <button
                  key={view?.id}
                  onClick={() => setActiveView(view?.id)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    activeView === view?.id
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name={view?.icon} size={16} />
                  <span>{view?.label}</span>
                </button>
              ))}
            </nav>

            <div className="flex items-center space-x-3">
              {selectedTemplate && (
                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <Icon name="FileText" size={16} />
                  <span>{selectedTemplate?.name}</span>
                </div>
              )}
              <Button
                variant="outline"
                onClick={handleSaveDraft}
                iconName="Save"
                iconPosition="left"
                disabled={!selectedTemplate}
              >
                Save Draft
              </Button>
              <Button
                variant="default"
                onClick={handleGenerateDocument}
                iconName="Zap"
                iconPosition="left"
                disabled={!selectedTemplate}
              >
                Generate Document
              </Button>
            </div>
          </div>

          {/* Content Area */}
          <div className="space-y-6">
            {activeView === 'templates' && (
              <div>
                <div className="mb-6">
                  <h2 className="text-xl font-semibold text-foreground mb-2">
                    Select Document Template
                  </h2>
                  <p className="text-muted-foreground">
                    Choose from our library of SAP-optimized document templates for BPD² and FSD² generation.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {templates?.map((template) => (
                    <DocumentTemplateCard
                      key={template?.id}
                      template={template}
                      onSelect={handleTemplateSelect}
                      isSelected={selectedTemplate?.id === template?.id}
                    />
                  ))}
                </div>

                {selectedTemplate && (
                  <div className="mt-8 p-6 bg-primary/5 border border-primary/20 rounded-lg">
                    <div className="flex items-center space-x-3 mb-4">
                      <Icon name="CheckCircle2" size={20} className="text-primary" />
                      <h3 className="text-lg font-semibold text-foreground">
                        Template Selected: {selectedTemplate?.name}
                      </h3>
                    </div>
                    <p className="text-muted-foreground mb-4">
                      {selectedTemplate?.description}
                    </p>
                    <Button
                      variant="default"
                      onClick={() => setActiveView('editor')}
                      iconName="ArrowRight"
                      iconPosition="right"
                    >
                      Configure Template
                    </Button>
                  </div>
                )}
              </div>
            )}

            {activeView === 'editor' && selectedTemplate && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-6">
                  <DocumentConfigPanel
                    template={selectedTemplate}
                    config={documentConfig}
                    onConfigChange={handleConfigChange}
                  />
                  <DiagramEditor
                    onDiagramChange={handleDiagramChange}
                    initialDiagram={diagramData}
                  />
                </div>
                <div>
                  <DocumentPreview
                    template={selectedTemplate}
                    config={documentConfig}
                    onExport={handleExport}
                  />
                </div>
              </div>
            )}

            {activeView === 'preview' && selectedTemplate && (
              <div className="max-w-4xl mx-auto">
                <DocumentPreview
                  template={selectedTemplate}
                  config={documentConfig}
                  onExport={handleExport}
                />
              </div>
            )}

            {activeView === 'versions' && (
              <div className="max-w-4xl mx-auto">
                <VersionControlPanel
                  onVersionSelect={handleVersionSelect}
                  currentVersion="1.3"
                />
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="fixed bottom-6 right-6 md:relative md:bottom-auto md:right-auto md:mt-8">
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                onClick={() => navigate('/fit-gap-board')}
                iconName="ArrowLeft"
                iconPosition="left"
                className="hidden md:flex"
              >
                Back to Fit/Gap
              </Button>
              <Button
                variant="default"
                onClick={() => navigate('/digital-signoffs')}
                iconName="PenTool"
                iconPosition="left"
                disabled={!selectedTemplate}
              >
                Send for Approval
              </Button>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default DocumentGenerator;